// Who is looking decides the wording: designers get workflow-centric labels.
export type BadgeViewer = "designer" | "actor" | "observer";

// One wording for every client-side "the design team owes you something" state.
export const WAITING_ON_DESIGN_TEAM_LABEL = "Waiting On Design Team";
export const PENDING_CLIENT_FEEDBACK_LABEL = "Pending Client Feedback";

const REVISION_STATUS_LABELS: Record<string, string> = {
  waiting_for_client_response: PENDING_CLIENT_FEEDBACK_LABEL,
  in_review: "Changes Received",
  in_progress: "In Progress",
  completed: "Completed",
};

const CLIENT_REVISION_STATUS_LABELS: Record<string, string> = {
  waiting_for_client_response: PENDING_CLIENT_FEEDBACK_LABEL,
  in_review: WAITING_ON_DESIGN_TEAM_LABEL,
  in_progress: WAITING_ON_DESIGN_TEAM_LABEL,
  completed: "Completed",
};

export const READY_TO_START_TYPE = "ready_to_start";
export const WAITING_ON_DESIGN_TEAM_TYPE = "waiting_on_design_team";
export const PENDING_CLIENT_FEEDBACK_TYPE = "pending_client_feedback";
export const COMPLETED_TYPE = "completed";

// Client-side colour follows the wording, so one label never shows two colours.
const CLIENT_BADGE_TYPES: Record<string, string> = {
  [WAITING_ON_DESIGN_TEAM_LABEL]: WAITING_ON_DESIGN_TEAM_TYPE,
  [PENDING_CLIENT_FEEDBACK_LABEL]: PENDING_CLIENT_FEEDBACK_TYPE,
  Completed: COMPLETED_TYPE,
};

const normalize = (value?: string | null): string =>
  (value ?? "").toLowerCase().replace(/[-\s]/g, "_");

// Preliminary with no revision yet: the designer owes the first upload.
const isAwaitingFirstRevision = (
  projectStatus?: string | null,
  hasRevision?: boolean,
): boolean => !hasRevision && normalize(projectStatus) === "preliminary";

export const getRevisionStatusLabel = (
  status?: string | null,
  projectStatus?: string | null,
  viewer: BadgeViewer = "designer",
  hasRevision: boolean = true,
): string | null => {
  if (isAwaitingFirstRevision(projectStatus, hasRevision)) {
    return viewer === "designer"
      ? "Ready To Start"
      : WAITING_ON_DESIGN_TEAM_LABEL;
  }
  if (!status) return null;
  const key = normalize(status);
  // Per client request: a completed revision reads as the next action for the stage.
  if (key === "completed" && projectStatus) {
    const projKey = normalize(projectStatus);
    if (projKey === "preliminary_finals") {
      return viewer === "designer"
        ? "Starting Finals"
        : WAITING_ON_DESIGN_TEAM_LABEL;
    }
    if (projKey === "final_review") {
      return viewer === "designer"
        ? "Team Review"
        : WAITING_ON_DESIGN_TEAM_LABEL;
    }
  }
  if (viewer === "designer") return REVISION_STATUS_LABELS[key] ?? null;
  return CLIENT_REVISION_STATUS_LABELS[key] ?? null;
};

export const getRevisionStatusType = (
  status?: string | null,
  projectStatus?: string | null,
  viewer: BadgeViewer = "designer",
  hasRevision: boolean = true,
): string => {
  if (isAwaitingFirstRevision(projectStatus, hasRevision)) {
    return viewer === "designer"
      ? READY_TO_START_TYPE
      : WAITING_ON_DESIGN_TEAM_TYPE;
  }
  if (viewer !== "designer") {
    const label = getRevisionStatusLabel(
      status,
      projectStatus,
      viewer,
      hasRevision,
    );
    if (label && CLIENT_BADGE_TYPES[label]) return CLIENT_BADGE_TYPES[label];
  }
  return status ?? "";
};
